import { createSelector } from "reselect";
import { isDefined, memoize, uniqueByKey } from "@ls/js-utils";
import SelectionState from "state/selections/SelectionState";
import scope from "./scope";
import SummaryType from "state/selections/Constants/SummaryType";

export const selectPerfReturnValues = (state) => state[scope].perfReturnValues;
export const selectData = (summaryType) => (state) =>
  state[scope]["data"][summaryType];
export const selectFetchingData = (summaryType) => (state) =>
  state[scope]["fetchingData"][summaryType];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return (state) => state[scope]["definitions"][selectionState];
  }

  return createSelector(
    (state) => state[scope]["definitions"][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(
        ({ complete }) => complete
      );
      return completeDefinitions.filter(
        uniqueByKey(completeDefinitions, "key")
      );
    }
  );
});

export const selectRowsMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  (perfReturnDefinitions, absData, relData) => {
    const toRowsMap = (groupingData, perfReturnDefinitions) => {
      const entityRowsMap = {};
      if (groupingData?.entityPerfReturns && perfReturnDefinitions?.length) {
        groupingData.entityPerfReturns.forEach((entityPerfReturn) => {
          const { netGross, type, entityId } = entityPerfReturn.primaryKey;
          perfReturnDefinitions.forEach((perfReturnDef) => {
            if (
              type === perfReturnDef.type &&
              netGross === perfReturnDef.netGross
            ) {
              if (!isDefined(entityRowsMap[entityId])) {
                entityRowsMap[entityId] = {};
              }
              entityRowsMap[entityId][
                `${perfReturnDef.type},${perfReturnDef.summary}`
              ] = entityPerfReturn.value;
            }
          });
        });
      }
      return entityRowsMap;
    };

    const absRowsMap = toRowsMap(
      absData,
      perfReturnDefinitions.filter(
        ({ summary }) => summary === SummaryType.ABSOLUTE
      )
    );
    const relRowsMap = toRowsMap(
      relData,
      perfReturnDefinitions.filter(
        ({ summary }) => summary === SummaryType.RELATIVE
      )
    );

    const result = { ...absRowsMap };
    Object.entries(relRowsMap).forEach(([entityId, relFields]) => {
      if (!result[entityId]) {
        result[entityId] = {};
      }
      result[entityId] = {
        ...result[entityId],
        ...relFields,
      };
    });

    return result;
  }
);
